import { useMemo, useState } from 'react';
import {
  SalesforceAutomation,
  SalesforceAutomationCondition,
} from 'api/integrations';
import {
  useCreateAutomationMutation,
  useUpdateAutomationMutation,
} from 'queries/salesforce-integration';
import {
  PartialSalesforceAutomationLocalData,
  SalesforceAutomationLocalData,
} from '../../types';
import { useAutomationData } from './use-automation-data';
import { useAutomationMutationHandler } from './use-automation-mutation-handler';
import { serialiseAutomationPayload } from './utils';

export type CreateProps = {
  integrationId: number;
  defaultValue?: SalesforceAutomationCondition;
  automationDetails?: PartialSalesforceAutomationLocalData;
  onChange: (val: PartialSalesforceAutomationLocalData) => void;
};

export type StatusTypes = 'idle' | 'error' | 'loading' | 'success';

const requiredFields = [
  'automationName',
  'salesforceObject',
  'triggers',
  'rocketlaneFields',
];

export const useCreateAutomationActions = (
  integrationId: number,
  automationById?: SalesforceAutomation,
) => {
  const [automationDetails, setAutomationDetails] =
    useState<Partial<SalesforceAutomationLocalData> | undefined>(
      automationById,
    );

  const { mutate: createAutomationMutation, status: creationStatus } =
    useCreateAutomationMutation();

  const { mutate: editAutomationMutation, status: updationStatus } =
    useUpdateAutomationMutation();

  const onChange = async (key, value) => {
    await setAutomationDetails((data) => ({ ...data, [key]: value }));
  };

  const {
    onSaveSuccess,
    onUpdateSuccess,
    onError: onCreateError,
  } = useAutomationMutationHandler(integrationId);

  const {
    salesforceObjects,
    salesforceObjectsStatus,
    salesforceTriggerFields,
    salesforceTriggerFieldsStatus,
    salesforceMatchFields,
    salesforceMatchFieldsStatus,
    salesforceActionFields,
    salesforceActionFieldsStatus,
    salesForceActionFieldMergedData,
  } = useAutomationData(
    integrationId,
    automationDetails?.salesforceObject?.value as string,
    automationById,
    onChange,
  );

  const getPayload = () =>
    serialiseAutomationPayload(integrationId, {
      ...automationDetails,
      rocketlaneFields: {
        ...automationDetails?.rocketlaneFields,
        teamMembers: automationDetails?.rocketlaneFields?.teamMembers
          ?.map((e) => e.valueStr)
          .join(','),
      },
    });
  const onAutomationCreate = () => {
    if (automationDetails) {
      const payload = getPayload();

      if (payload) {
        createAutomationMutation(payload, {
          onSuccess: onSaveSuccess,
          onError: onCreateError,
        });
      }
    }
  };

  const onAutomationUpdate = () => {
    if (automationDetails && automationDetails.automationId) {
      const payload = getPayload();

      if (payload) {
        editAutomationMutation(
          {
            ...payload,
            automationId: automationDetails.automationId,
          },
          {
            onSuccess: onUpdateSuccess,
            onError: onCreateError,
          },
        );
      }
    }
  };

  const disableCreation = useMemo(() => {
    const automatioKeys = Object.keys(automationDetails ?? {});

    if (automatioKeys.length > 0) {
      return !requiredFields.every(
        (field) => automatioKeys.indexOf(field) >= 0,
      );
    }

    return true;
  }, [automationDetails]);

  return {
    automationDetails,
    disableCreation,
    creationStatus,
    updationStatus,
    setAutomationDetails,
    onAutomationCreate,
    onAutomationUpdate,
    salesforceObjects,
    salesforceObjectsStatus,
    salesforceTriggerFields,
    salesforceTriggerFieldsStatus,
    salesforceMatchFields,
    salesforceMatchFieldsStatus,
    salesforceActionFields,
    salesforceActionFieldsStatus,
    salesForceActionFieldMergedData,
  };
};
